import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "./customizeSpotTheme.css";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken } from "../../sessionSlice";
import { updateThemeData } from "../../functions/updateThemeData";
import { apiUrl } from "../../../utils/apiUrl";
import {
  countQuestionsInRounds,
  parseRoundsFromThemeData,
  validateQuizRoundsForSave,
  sanitizeQuizRoundsForSave,
  migrateQuizRoundsForAdmin,
} from "../../../utils/quizTheme";
import QuizRoundsEditor from "./QuizRoundsEditor";

const ITEM_W = 400;
const ITEM_H = 400;
const ITEM_NAME_MIN_LEN = 4;
const ITEM_NAME_MAX_LEN = 40;
const COUNTRY_NAME_MIN_LEN = 4;
const COUNTRY_NAME_MAX_LEN = 20;
const CATEGORY_NAME_MIN_LEN = 5;
const CATEGORY_NAME_MAX_LEN = 20;
const SPOT_ITEM_ID_MAX_LEN = 80;
const MAX_CATEGORIES_LIST_COUNT = 4;
const MAX_ITEMS_PER_CATEGORY = 8;

function toSlugKey(name) {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
}
function formatKey(key) {
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function validateLengthRange(value, minLen, maxLen) {
  const len = String(value || "").trim().length;
  return len >= minLen && len <= maxLen;
}

/** Categories with an odd item count need one more item to complete a pair. */
function getFirstIncompletePairCategory(cats) {
  const idx = cats.findIndex((c) => c.items.length % 2 === 1);
  if (idx === -1) return null;
  return { catIndex: idx, displayName: cats[idx].displayName };
}

/** Renders an item image with a proper fallback.
 *  Resets error state whenever `src` changes (e.g. after a new upload),
 *  so stale inline-style display:none never survives a src update. */
function ItemImage({ src, alt, className }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [src]);

  if (!src || failed) {
    return (
      <div className={`catmgr-item-no-img ${className || ""}`}>
        <i className="fa-solid fa-image" />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      className={`catmgr-item-img ${className || ""}`}
      referrerPolicy="no-referrer"
      onError={() => setFailed(true)}
    />
  );
}

const CustomizeSpotTheme = () => {
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { currentTheme, data } = useSelector((s) => s.theme);

  // categories: [{ key, displayName, items:[{image,name}] }]
  const [categories, setCategories]   = useState([]);
  const [useQuizRounds, setUseQuizRounds] = useState(false);
  const [quizRounds, setQuizRounds]     = useState([]);
  const [saving, setSaving]           = useState(false);
  const [spotMetaSaving, setSpotMetaSaving] = useState(false);
  const [countriesList, setCountriesList] = useState([]);
  const [categoriesListMeta, setCategoriesListMeta] = useState([]);
  const [structuredCategories, setStructuredCategories] = useState([]);
  const [activeStructuredTab, setActiveStructuredTab] = useState("");

  // ── Category modals ─────────────────────────────────────────
  const [addCatOpen,   setAddCatOpen]   = useState(false);
  const [newCatName,   setNewCatName]   = useState("");
  const [editCatModal, setEditCatModal] = useState(null); // { index }
  const [editCatName,  setEditCatName]  = useState("");
  const [listModal, setListModal] = useState(null); // { mode, listType, index? }
  const [listNameInput, setListNameInput] = useState("");
  const [listImageFile, setListImageFile] = useState(null);
  const [listImagePreview, setListImagePreview] = useState("");
  const [listImageFilename, setListImageFilename] = useState("");
  const [listImageUploading, setListImageUploading] = useState(false);
  const listImageFileRef = useRef(null);

  // ── Item modal (shared add/edit) ────────────────────────────
  // mode: 'add' | 'edit'
  const [itemModal,    setItemModal]    = useState(null); // { mode, catIndex, itemIndex? }
  const [itemName,     setItemName]     = useState("");
  const [itemFile,     setItemFile]     = useState(null);   // File object
  const [itemPreview,  setItemPreview]  = useState("");     // blob URL for preview
  const [itemFilename, setItemFilename] = useState("");     // existing filename (edit mode)
  const [itemUploading,setItemUploading]= useState(false);
  const itemFileRef = useRef(null);
  const [structuredItemModal, setStructuredItemModal] = useState(null); // { mode, catIndex, itemIndex? }
  const [structuredItemName, setStructuredItemName] = useState("");
  const [structuredItemCountryId, setStructuredItemCountryId] = useState("");
  const [structuredItemCategoryId, setStructuredItemCategoryId] = useState("");
  const [structuredItemFile, setStructuredItemFile] = useState(null);
  const [structuredItemPreview, setStructuredItemPreview] = useState("");
  const [structuredItemFilename, setStructuredItemFilename] = useState("");
  const [structuredItemUploading, setStructuredItemUploading] = useState(false);
  const structuredItemFileRef = useRef(null);

  // ── Load data ────────────────────────────────────────────────
  useEffect(() => {
    if (currentTheme) dispatch(fetchThemeData({ themeId: currentTheme }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (!data) return;
    if ("countriesList" in data) setCountriesList(Array.isArray(data.countriesList) ? data.countriesList : []);
    if ("categoriesList" in data) {
      setCategoriesListMeta(Array.isArray(data.categoriesList) ? data.categoriesList : []);
    }
    const hasStructured = Array.isArray(data.categories) && data.categories.some((c) => c && typeof c === "object");
    if (hasStructured) {
      const normalized = data.categories.map((cat) => ({
          id: String(cat.id || toSlugKey(cat.name || "category")),
          name: String(cat.name || "Category"),
          maxItems: Number(cat.maxItems || 8),
          items: Array.isArray(cat.items)
            ? cat.items.map((item) => ({
                id: String(item.id || toSlugKey(item.name || "item")),
                name: String(item.name || ""),
                image: String(item.image || ""),
                countryId: String(item.countryId || ""),
                mapId: String(item.mapId || item.countryId || ""),
              }))
            : [],
        }));
      setStructuredCategories(normalized);
      setActiveStructuredTab((prev) => {
        if (prev && normalized.some((c) => c.id === prev)) return prev;
        return normalized[0]?.id || "";
      });
    } else {
      setStructuredCategories([]);
      setActiveStructuredTab("");
    }
    const parsed = parseRoundsFromThemeData(data);
    if (parsed.length > 0) {
      setUseQuizRounds(true);
      setCategories([]);
      setQuizRounds(migrateQuizRoundsForAdmin(JSON.parse(JSON.stringify(parsed))));
      return;
    }
    setUseQuizRounds(false);
    setQuizRounds([]);
    // Avoid wiping local list when API returns a partial theme object without this key.
    if (!("category_images_names" in data)) return;
    const cin  = data.category_images_names || {};
    const cats = ("categories" in data) ? (data.categories || []) : [];
    const result = [];
    cats.forEach((displayName) => {
      const key = toSlugKey(displayName);
      result.push({ key, displayName, items: [...(cin[key] || [])] });
    });
    Object.keys(cin).forEach((key) => {
      if (!result.find((r) => r.key === key))
        result.push({ key, displayName: formatKey(key), items: [...(cin[key] || [])] });
    });
    setCategories(result);
  }, [data]);

  // ── Save all ─────────────────────────────────────────────────
  const handleSave = async (cats) => {
    if (!currentTheme) { Swal.fire("No theme", "Select a theme first.", "error"); return; }
    setSaving(true);
    try {
      await updateThemeData({
        payload: {
          data: {
            categories: cats.map((c) => c.displayName),
            category_images_names: Object.fromEntries(cats.map((c) => [c.key, c.items])),
          },
          currentTheme,
        },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
      Swal.fire("Saved", "Categories updated.", "success");
    } catch (e) {
      Swal.fire("Error", e.message || "Save failed.", "error");
    }
    setSaving(false);
  };

  const handleSaveRounds = async () => {
    if (!currentTheme) {
      Swal.fire("No theme", "Select a theme first.", "error");
      return;
    }
    const err = validateQuizRoundsForSave(quizRounds);
    if (err) {
      Swal.fire("Check your content", err, "warning");
      return;
    }
    const cleaned = sanitizeQuizRoundsForSave(quizRounds);
    const total = countQuestionsInRounds(cleaned);
    setSaving(true);
    try {
      await updateThemeData({
        payload: {
          data: {
            rounds: cleaned,
            number_of_slides: total,
          },
          currentTheme,
        },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
      Swal.fire("Saved", "Quiz rounds updated.", "success");
    } catch (e) {
      Swal.fire("Error", e.message || "Save failed.", "error");
    }
    setSaving(false);
  };

  const listLabel = (type) => (type === "countries" ? "country" : "category");
  const visibleStructuredCategories = useMemo(() => {
    if (!structuredCategories.length) return [];
    if (!categoriesListMeta.length) return structuredCategories;
    const byId = new Map(structuredCategories.map((cat) => [String(cat?.id || ""), cat]));
    const usedIds = new Set();
    const merged = categoriesListMeta
      .map((meta) => {
        const metaId = String(meta?.id || "");
        const metaName = String(meta?.name || "");
        const metaIdSlug = toSlugKey(metaId);
        const metaNameSlug = toSlugKey(metaName);
        let match = byId.get(metaId);
        if (!match) {
          match =
            structuredCategories.find((cat) => {
              const catIdSlug = toSlugKey(String(cat?.id || ""));
              const catNameSlug = toSlugKey(String(cat?.name || ""));
              return (
                catIdSlug === metaIdSlug ||
                catIdSlug === metaNameSlug ||
                catNameSlug === metaIdSlug ||
                catNameSlug === metaNameSlug
              );
            }) || null;
        }
        if (match && !usedIds.has(String(match.id))) {
          usedIds.add(String(match.id));
          return { ...match, name: metaName || match.name };
        }
        if (!metaId) return null;
        return {
          id: metaId,
          name: metaName || "Category",
          maxItems: MAX_ITEMS_PER_CATEGORY,
          items: [],
        };
      })
      .filter(Boolean);
    return merged.length ? merged : structuredCategories;
  }, [structuredCategories, categoriesListMeta]);
  const hasStructuredSpotData = visibleStructuredCategories.length > 0 && countriesList.length > 0;
  const countryNameById = useMemo(
    () =>
      new Map(
        countriesList.map((country) => [
          String(country?.id || ""),
          String(country?.name || ""),
        ])
      ),
    [countriesList]
  );
  const activeStructuredCategory = hasStructuredSpotData
    ? visibleStructuredCategories.find((c) => c.id === activeStructuredTab) || visibleStructuredCategories[0]
    : null;

  useEffect(() => {
    if (!visibleStructuredCategories.length) {
      setActiveStructuredTab("");
      return;
    }
    setActiveStructuredTab((prev) => {
      if (prev && visibleStructuredCategories.some((c) => c.id === prev)) return prev;
      return visibleStructuredCategories[0]?.id || "";
    });
  }, [visibleStructuredCategories]);

  const handleSaveSpotMeta = async (nextCountries, nextCategories, nextStructuredCategories = null) => {
    if (!currentTheme) {
      Swal.fire("No theme", "Select a theme first.", "error");
      return false;
    }
    setSpotMetaSaving(true);
    try {
      await updateThemeData({
        payload: {
          data: {
            countriesList: nextCountries,
            categoriesList: nextCategories,
            ...(Array.isArray(nextStructuredCategories) ? { categories: nextStructuredCategories } : {}),
          },
          currentTheme,
        },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
      return true;
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save list changes.", "error");
      return false;
    } finally {
      setSpotMetaSaving(false);
    }
  };

  const openAddListModal = (listType) => {
    if (listType === "categories" && categoriesListMeta.length >= MAX_CATEGORIES_LIST_COUNT) {
      Swal.fire(
        "Limit reached",
        `Categories List can have maximum ${MAX_CATEGORIES_LIST_COUNT} categories only.`,
        "warning"
      );
      return;
    }
    setListModal({ mode: "add", listType });
    setListNameInput("");
    setListImageFile(null);
    setListImagePreview("");
    setListImageFilename("");
    if (listImageFileRef.current) listImageFileRef.current.value = "";
  };

  const openEditListModal = (listType, index, currentItem) => {
    setListModal({ mode: "edit", listType, index });
    setListNameInput(String(currentItem?.name || ""));
    setListImageFile(null);
    setListImagePreview("");
    setListImageFilename(String(currentItem?.image || ""));
    if (listImageFileRef.current) listImageFileRef.current.value = "";
  };

  const closeListModal = () => {
    if (listImagePreview) URL.revokeObjectURL(listImagePreview);
    setListModal(null);
    setListNameInput("");
    setListImageFile(null);
    setListImagePreview("");
    setListImageFilename("");
    if (listImageFileRef.current) listImageFileRef.current.value = "";
  };

  const handleListImageFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["jpeg", "jpg", "png"].includes(ext)) {
      Swal.fire("Invalid File", "Only JPEG / JPG / PNG allowed.", "error");
      e.target.value = "";
      return;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      if (img.width !== ITEM_W || img.height !== ITEM_H) {
        URL.revokeObjectURL(url);
        Swal.fire(
          "Wrong Dimensions",
          `Image must be exactly ${ITEM_W} × ${ITEM_H} px. Uploaded image is ${img.width} × ${img.height} px.`,
          "error"
        );
        if (listImageFileRef.current) listImageFileRef.current.value = "";
        return;
      }
      setListImageFile(file);
      setListImagePreview(url);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      Swal.fire("Invalid Image", "Could not read image dimensions. Please try another file.", "error");
      if (listImageFileRef.current) listImageFileRef.current.value = "";
    };
    img.src = url;
  };

  const uploadListCategoryImage = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("themeName", String(currentTheme));
    const res = await fetch(apiUrl("/uploadCategoryImage"), {
      method: "POST",
      headers: { Authorization: `Bearer ${adminToken}` },
      body: fd,
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Upload failed");
    return json.filename;
  };

  const handleSaveListItem = async () => {
    if (!listModal) return;
    const nextName = String(listNameInput || "").trim();
    if (!nextName) {
      Swal.fire("Required", `${formatKey(listLabel(listModal.listType))} name is required.`, "warning");
      return;
    }
    const minLen = listModal.listType === "countries" ? COUNTRY_NAME_MIN_LEN : CATEGORY_NAME_MIN_LEN;
    const maxLen = listModal.listType === "countries" ? COUNTRY_NAME_MAX_LEN : CATEGORY_NAME_MAX_LEN;
    if (!validateLengthRange(nextName, minLen, maxLen)) {
      Swal.fire(
        "Invalid name",
        `${formatKey(listLabel(listModal.listType))} name must be ${minLen}-${maxLen} characters.`,
        "warning"
      );
      return;
    }
    const targetList = listModal.listType === "countries" ? countriesList : categoriesListMeta;
    if (
      listModal.mode === "add" &&
      listModal.listType === "categories" &&
      targetList.length >= MAX_CATEGORIES_LIST_COUNT
    ) {
      Swal.fire(
        "Limit reached",
        `Categories List can have maximum ${MAX_CATEGORIES_LIST_COUNT} categories only.`,
        "warning"
      );
      return;
    }
    const duplicateIndex = targetList.findIndex(
      (it, idx) =>
        idx !== listModal.index &&
        String(it?.name || "").trim().toLowerCase() === nextName.toLowerCase()
    );
    if (duplicateIndex >= 0) {
      Swal.fire("Duplicate", `This ${listLabel(listModal.listType)} already exists.`, "warning");
      return;
    }
    let imageFilename = listImageFilename;
    if (listModal.listType === "categories" && listImageFile) {
      setListImageUploading(true);
      try {
        imageFilename = await uploadListCategoryImage(listImageFile);
      } catch (e) {
        Swal.fire("Upload Error", e.message || "Image upload failed.", "error");
        setListImageUploading(false);
        return;
      }
      setListImageUploading(false);
    }
    const existingItem = targetList[listModal.index] || null;
    const nextItem = {
      id:
        listModal.mode === "edit" && listModal.listType === "categories"
          ? String(existingItem?.id || toSlugKey(nextName))
          : toSlugKey(nextName),
      name: nextName,
      ...(listModal.listType === "categories" && imageFilename ? { image: imageFilename } : {}),
    };
    const nextCountries =
      listModal.listType === "countries"
        ? listModal.mode === "add"
          ? [...countriesList, nextItem]
          : countriesList.map((item, idx) => (idx === listModal.index ? nextItem : item))
        : countriesList;
    const nextCategories =
      listModal.listType === "categories"
        ? listModal.mode === "add"
          ? [...categoriesListMeta, nextItem]
          : categoriesListMeta.map((item, idx) => (idx === listModal.index ? nextItem : item))
        : categoriesListMeta;
    const nextStructuredCategories =
      listModal.listType === "categories"
        ? listModal.mode === "edit"
          ? structuredCategories.map((cat) =>
              String(cat.id) === String(nextItem.id) ? { ...cat, name: nextItem.name } : cat
            )
          : structuredCategories.some((cat) => String(cat.id) === String(nextItem.id))
            ? structuredCategories
            : [
                ...structuredCategories,
                {
                  id: String(nextItem.id),
                  name: String(nextItem.name || "Category"),
                  maxItems: MAX_ITEMS_PER_CATEGORY,
                  items: [],
                },
              ]
        : null;
    const ok = await handleSaveSpotMeta(nextCountries, nextCategories, nextStructuredCategories);
    if (!ok) return;
    setCountriesList(nextCountries);
    setCategoriesListMeta(nextCategories);
    if (Array.isArray(nextStructuredCategories)) setStructuredCategories(nextStructuredCategories);
    closeListModal();
    Swal.fire("Saved", `${formatKey(listLabel(listModal.listType))} updated.`, "success");
  };

  const handleDeleteListItem = (listType, index) => {
    const currentList = listType === "countries" ? countriesList : categoriesListMeta;
    const itemName = String(currentList[index]?.name || "this item");
    const isCategoryDelete = listType === "categories";
    Swal.fire({
      title: isCategoryDelete ? "Delete category?" : "Delete item?",
      html: isCategoryDelete
        ? `"${itemName}" will be removed from Categories List.<br/><br/>
           <strong>Warning:</strong> any Spot Map mappings using this category may lose linked assignment data.`
        : `"${itemName}" will be removed from Country List.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: isCategoryDelete ? "Yes, delete category" : "Yes, delete",
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      const nextCountries =
        listType === "countries" ? countriesList.filter((_, idx) => idx !== index) : countriesList;
      const nextCategories =
        listType === "categories"
          ? categoriesListMeta.filter((_, idx) => idx !== index)
          : categoriesListMeta;
      const deletedCategoryId = String(categoriesListMeta[index]?.id || "");
      const nextStructuredCategories =
        listType === "categories"
          ? structuredCategories.filter((cat) => String(cat.id) !== deletedCategoryId)
          : null;
      const ok = await handleSaveSpotMeta(nextCountries, nextCategories, nextStructuredCategories);
      if (!ok) return;
      setCountriesList(nextCountries);
      setCategoriesListMeta(nextCategories);
      if (Array.isArray(nextStructuredCategories)) setStructuredCategories(nextStructuredCategories);
      Swal.fire("Deleted", "Item removed.", "success");
    });
  };

  const handleStructuredItemFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["jpeg", "jpg", "png"].includes(ext)) {
      Swal.fire("Invalid File", "Only JPEG / JPG / PNG allowed.", "error");
      e.target.value = "";
      return;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      if (img.width !== ITEM_W || img.height !== ITEM_H) {
        URL.revokeObjectURL(url);
        Swal.fire(
          "Wrong Dimensions",
          `Image must be exactly ${ITEM_W} × ${ITEM_H} px. Uploaded image is ${img.width} × ${img.height} px.`,
          "error"
        );
        if (structuredItemFileRef.current) structuredItemFileRef.current.value = "";
        return;
      }
      setStructuredItemFile(file);
      setStructuredItemPreview(url);
    };
    img.src = url;
  };

  const closeStructuredItemModal = () => {
    if (structuredItemPreview) URL.revokeObjectURL(structuredItemPreview);
    setStructuredItemModal(null);
    setStructuredItemName("");
    setStructuredItemCountryId("");
    setStructuredItemCategoryId("");
    setStructuredItemFile(null);
    setStructuredItemPreview("");
    setStructuredItemFilename("");
    if (structuredItemFileRef.current) structuredItemFileRef.current.value = "";
  };

  const handleSaveStructuredCategories = async (nextCategories) => {
    if (!currentTheme) {
      Swal.fire("No theme", "Select a theme first.", "error");
      return false;
    }
    setSaving(true);
    try {
      await updateThemeData({
        payload: {
          data: { categories: nextCategories },
          currentTheme,
        },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
      setStructuredCategories(nextCategories);
      return true;
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save spot items.", "error");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const openStructuredAddItemModal = (catIndex) => {
    const cat = structuredCategories[catIndex];
    if (!cat) return;
    if ((cat.items || []).length >= (Number(cat.maxItems) || MAX_ITEMS_PER_CATEGORY)) {
      Swal.fire(
        "Limit reached",
        `You can add maximum ${Number(cat.maxItems) || MAX_ITEMS_PER_CATEGORY} items in this category.`,
        "warning"
      );
      return;
    }
    setStructuredItemModal({ mode: "add", catIndex });
    setStructuredItemName("");
    setStructuredItemCategoryId(cat.id);
    setStructuredItemCountryId(countriesList[0]?.id || "");
    setStructuredItemFilename("");
    setStructuredItemFile(null);
    setStructuredItemPreview("");
  };

  const openStructuredEditItemModal = (catIndex, itemIndex) => {
    const item = structuredCategories[catIndex]?.items?.[itemIndex];
    if (!item) return;
    const sourceCategory = structuredCategories[catIndex] || null;
    setStructuredItemModal({ mode: "edit", catIndex, itemIndex });
    setStructuredItemName(String(item.name || "").slice(0, ITEM_NAME_MAX_LEN));
    setStructuredItemCategoryId(String(sourceCategory?.id || ""));
    setStructuredItemCountryId(item.countryId || countriesList[0]?.id || "");
    setStructuredItemFilename(item.image || "");
    setStructuredItemFile(null);
    setStructuredItemPreview("");
  };

  const handleSaveStructuredItem = async () => {
    if (!structuredItemModal) return;
    const name = String(structuredItemName || "").trim();
    if (!name) {
      Swal.fire("Required", "Item name is required.", "warning");
      return;
    }
    if (!validateLengthRange(name, ITEM_NAME_MIN_LEN, ITEM_NAME_MAX_LEN)) {
      Swal.fire("Invalid name", `Item name must be ${ITEM_NAME_MIN_LEN}-${ITEM_NAME_MAX_LEN} characters.`, "warning");
      return;
    }
    if (!structuredItemCountryId) {
      Swal.fire("Required", "Please select a country.", "warning");
      return;
    }
    if (!structuredItemCategoryId) {
      Swal.fire("Required", "Please select a category.", "warning");
      return;
    }
    let filename = structuredItemFilename;
    if (structuredItemFile) {
      setStructuredItemUploading(true);
      try {
        filename = await uploadItemImage(structuredItemFile);
      } catch (e) {
        Swal.fire("Upload Error", e.message || "Upload failed.", "error");
        setStructuredItemUploading(false);
        return;
      }
      setStructuredItemUploading(false);
    }
    if (!filename) {
      Swal.fire("Image required", "Please upload an image.", "warning");
      return;
    }
    const itemIdBase = toSlugKey(name).slice(0, SPOT_ITEM_ID_MAX_LEN) || "item";
    const nextCategories = structuredCategories.map((cat) => ({
      ...cat,
      items: [...cat.items],
    }));
    if (structuredItemModal.mode === "add") {
      const targetIndex = nextCategories.findIndex((c) => c.id === structuredItemCategoryId);
      if (targetIndex < 0) {
        Swal.fire("Invalid category", "Please choose a valid category.", "warning");
        return;
      }
      const maxItems = Number(nextCategories[targetIndex].maxItems) || MAX_ITEMS_PER_CATEGORY;
      if (nextCategories[targetIndex].items.length >= maxItems) {
        Swal.fire("Limit reached", `Maximum ${maxItems} items are allowed in this category.`, "warning");
        return;
      }
      nextCategories[targetIndex].items.push({
        id: `${itemIdBase}_${Date.now()}`,
        name,
        image: filename,
        countryId: structuredItemCountryId,
        mapId: structuredItemCountryId,
      });
    } else {
      const sourceCat = nextCategories[structuredItemModal.catIndex];
      const currentItem = sourceCat?.items?.[structuredItemModal.itemIndex];
      if (!sourceCat || !currentItem) return;
      sourceCat.items = sourceCat.items.filter((_, idx) => idx !== structuredItemModal.itemIndex);
      const targetIndex = nextCategories.findIndex((c) => c.id === structuredItemCategoryId);
      if (targetIndex < 0) return;
      const maxItems = Number(nextCategories[targetIndex].maxItems) || MAX_ITEMS_PER_CATEGORY;
      if (nextCategories[targetIndex].items.length >= maxItems) {
        Swal.fire("Limit reached", `Maximum ${maxItems} items are allowed in this category.`, "warning");
        return;
      }
      nextCategories[targetIndex].items.push({
        ...currentItem,
        id: currentItem.id || itemIdBase,
        name,
        image: filename,
        countryId: structuredItemCountryId,
        mapId: structuredItemCountryId,
      });
    }
    const ok = await handleSaveStructuredCategories(nextCategories);
    if (!ok) return;
    closeStructuredItemModal();
    Swal.fire("Saved", "Spot item updated.", "success");
  };

  const handleDeleteStructuredItem = (catIndex, itemIndex) => {
    const item = structuredCategories[catIndex]?.items?.[itemIndex];
    if (!item) return;
    Swal.fire({
      title: "Delete item?",
      text: `"${item.name}" will be removed.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
    }).then(async (r) => {
      if (!r.isConfirmed) return;
      const nextCategories = structuredCategories.map((cat, idx) =>
        idx === catIndex ? { ...cat, items: cat.items.filter((_, ii) => ii !== itemIndex) } : cat
      );
      const ok = await handleSaveStructuredCategories(nextCategories);
      if (!ok) return;
      Swal.fire("Deleted", "Item removed.", "success");
    });
  };

  // ── Category CRUD ────────────────────────────────────────────
  const handleAddCategory = async () => {
    const name = newCatName.trim();
    if (!name) { Swal.fire("Required", "Category name is required.", "warning"); return; }
    if (!validateLengthRange(name, CATEGORY_NAME_MIN_LEN, CATEGORY_NAME_MAX_LEN)) {
      Swal.fire("Invalid name", `Category name must be ${CATEGORY_NAME_MIN_LEN}-${CATEGORY_NAME_MAX_LEN} characters.`, "warning");
      return;
    }
    const key = toSlugKey(name);
    if (categories.find((c) => c.key === key)) {
      Swal.fire("Duplicate", "A category with this name already exists.", "warning"); return;
    }
    const updatedCats = [...categories, { key, displayName: name, items: [] }];
    setCategories(updatedCats);
    setAddCatOpen(false);
    setNewCatName("");
    await handleSave(updatedCats);
  };

  const handleEditCategory = async () => {
    const name = editCatName.trim();
    if (!name) { Swal.fire("Required", "Category name is required.", "warning"); return; }
    if (!validateLengthRange(name, CATEGORY_NAME_MIN_LEN, CATEGORY_NAME_MAX_LEN)) {
      Swal.fire("Invalid name", `Category name must be ${CATEGORY_NAME_MIN_LEN}-${CATEGORY_NAME_MAX_LEN} characters.`, "warning");
      return;
    }
    const updatedCats = categories.map((c, i) => i === editCatModal.index ? { ...c, displayName: name } : c);
    setCategories(updatedCats);
    setEditCatModal(null);
    setEditCatName("");
    await handleSave(updatedCats);
  };

  const handleDeleteCategory = (index) => {
    Swal.fire({
      title: "Delete Category?",
      text: `"${categories[index].displayName}" and all its items will be removed.`,
      icon: "warning", showCancelButton: true, confirmButtonText: "Yes, delete",
    }).then(async (r) => {
      if (r.isConfirmed) {
        const updatedCats = categories.filter((_, i) => i !== index);
        setCategories(updatedCats);
        await handleSave(updatedCats);
      }
    });
  };

  // ── Item image upload helper ──────────────────────────────────
  const uploadItemImage = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("themeName", String(currentTheme));
    const res  = await fetch(apiUrl("/uploadCategoryImage"), {
      method: "POST",
      headers: { Authorization: `Bearer ${adminToken}` },
      body: fd,
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Upload failed");
    return json.filename;
  };

  // ── Item file picker (with dimension validation) ──────────────
  const handleItemFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["jpeg", "jpg", "png"].includes(ext)) {
      Swal.fire("Invalid File", "Only JPEG / JPG / PNG allowed.", "error");
      e.target.value = ""; return;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      if (img.width !== ITEM_W || img.height !== ITEM_H) {
        URL.revokeObjectURL(url);
        Swal.fire(
          "Wrong Dimensions",
          `Image must be exactly ${ITEM_W} × ${ITEM_H} px. Uploaded image is ${img.width} × ${img.height} px.`,
          "error"
        );
        if (itemFileRef.current) itemFileRef.current.value = "";
        return;
      }
      setItemFile(file);
      setItemPreview(url);
    };
    img.src = url;
  };

  const closeItemModal = () => {
    if (itemPreview) URL.revokeObjectURL(itemPreview);
    setItemModal(null);
    setItemName(""); setItemFile(null); setItemPreview(""); setItemFilename("");
    if (itemFileRef.current) itemFileRef.current.value = "";
  };

  /** Block adding to another category while a pair is incomplete elsewhere. */
  const assertCanAddItemToCategory = (catIndex) => {
    const pending = getFirstIncompletePairCategory(categories);
    if (!pending || pending.catIndex === catIndex) return true;
    Swal.fire({
      icon: "warning",
      title: "Complete the pair first",
      html:
        `Please add <strong>one more item</strong> in <strong>${pending.displayName}</strong> to make a pair before adding items to <strong>${categories[catIndex].displayName}</strong>.`,
    });
    return false;
  };

  const handleItemNameChange = (val) => {
    let next = val;
    if (next.length > ITEM_NAME_MAX_LEN) {
      Swal.fire(
        "Limit Exceeded",
        `Item name can be at most ${ITEM_NAME_MAX_LEN} characters. Extra text has been trimmed.`,
        "warning"
      );
      next = next.slice(0, ITEM_NAME_MAX_LEN);
    }
    setItemName(next);
  };

  const openAddItemModal = (catIndex) => {
    if (!assertCanAddItemToCategory(catIndex)) return;
    setItemModal({ mode: "add", catIndex });
    setItemName("");
    setItemFile(null);
    setItemPreview("");
    setItemFilename("");
  };

  // ── Item CRUD ─────────────────────────────────────────────────
  const handleSaveItem = async () => {
    const trimmed = itemName.trim();
    if (!trimmed) {
      Swal.fire("Required", "Item name is required.", "warning");
      return;
    }
    if (!validateLengthRange(trimmed, ITEM_NAME_MIN_LEN, ITEM_NAME_MAX_LEN)) {
      Swal.fire(
        "Invalid name",
        `Item name must be ${ITEM_NAME_MIN_LEN}-${ITEM_NAME_MAX_LEN} characters.`,
        "warning"
      );
      return;
    }
    const name = trimmed;

    const { mode, catIndex, itemIndex } = itemModal;

    if (mode === "add" && !assertCanAddItemToCategory(catIndex)) {
      return;
    }

    if (mode === "add" && !itemFile) {
      Swal.fire("Image required", "Please choose an image file for the new item (name and image are both required).", "warning");
      return;
    }

    let filename = itemFilename;
    if (itemFile) {
      setItemUploading(true);
      try { filename = await uploadItemImage(itemFile); }
      catch (e) { Swal.fire("Upload Error", e.message, "error"); setItemUploading(false); return; }
      setItemUploading(false);
    }

    if (mode === "add" && !filename) {
      Swal.fire("Image required", "Image upload failed or is missing. Please try again.", "warning");
      return;
    }
    if (mode === "edit" && !filename) {
      Swal.fire("Image required", "Each item must have an image.", "warning");
      return;
    }

    let updatedCats;
    if (mode === "add") {
      updatedCats = categories.map((c, i) =>
        i === catIndex ? { ...c, items: [...c.items, { image: filename, name }] } : c
      );
    } else {
      updatedCats = categories.map((c, ci) =>
        ci === catIndex
          ? { ...c, items: c.items.map((it, ii) => ii === itemIndex ? { image: filename, name } : it) }
          : c
      );
    }

    setCategories(updatedCats);
    closeItemModal();
    await handleSave(updatedCats);
  };

  const handleDeleteItem = (catIndex, itemIndex) => {
    Swal.fire({
      title: "Delete Item?",
      text: `"${categories[catIndex].items[itemIndex].name}" will be removed.`,
      icon: "warning", showCancelButton: true, confirmButtonText: "Yes, delete",
    }).then(async (r) => {
      if (r.isConfirmed) {
        const updatedCats = categories.map((c, ci) =>
          ci === catIndex ? { ...c, items: c.items.filter((_, ii) => ii !== itemIndex) } : c
        );
        setCategories(updatedCats);
        await handleSave(updatedCats);
      }
    });
  };

  const S3        = process.env.REACT_APP_S3_PATH || "";
  const resolveAssetUrl = (path) => {
    const raw = String(path || "").trim();
    if (!raw) return "";
    if (/^https?:\/\//i.test(raw)) return raw;
    const rel = raw.replace(/^\/+/, "");
    return `${S3}${encodeURIComponent(rel).replace(/%2F/g, "/")}`;
  };
  const gameName  = data?.themeName || data?.themename || "World Charms";
  const totalItems = hasStructuredSpotData
    ? visibleStructuredCategories.reduce((s, c) => s + (c.items?.length || 0), 0)
    : categories.reduce((s, c) => s + c.items.length, 0);
  const totalCategoryCount = hasStructuredSpotData ? visibleStructuredCategories.length : categories.length;
  const quizQuestionTotal = useQuizRounds ? countQuestionsInRounds(quizRounds) : 0;

  return (
    <>
      <div className="back-button-holder">
        <button className="back-button" onClick={() => navigate("/admin/rules")}>
          <i className="fa-solid fa-arrow-left" /> Back
        </button>
      </div>

      <div className={`catmgr-page${useQuizRounds ? " catmgr-page--quiz" : ""}`}>
        {useQuizRounds ? (
          <>
            <header className="catmgr-header catmgr-header--quiz">
              <div className="catmgr-header-left">
                <h2 className="catmgr-title catmgr-title--quiz">
                  <span className="catmgr-title-icon" aria-hidden>
                    <i className="fa-solid fa-circle-question" />
                  </span>
                  {gameName}
                  <span className="catmgr-title-sep">—</span>
                  <span className="catmgr-title-muted">Quiz rounds</span>
                </h2>
                <p className="catmgr-subtitle catmgr-subtitle--quiz">
                  Configure each round below. Every round allows 1–10 questions (the last question in a round cannot
                  be removed). Use <strong>Save all rounds</strong> when you are done.
                </p>
              </div>
              <div className="catmgr-header-right catmgr-header-right--quiz">
                <span className="catmgr-stat-pill" title="Total questions across all rounds">
                  {quizQuestionTotal} questions
                </span>
              </div>
            </header>
            <QuizRoundsEditor
              rounds={quizRounds}
              setRounds={setQuizRounds}
              saving={saving}
              onSave={handleSaveRounds}
              adminToken={adminToken}
              currentTheme={currentTheme}
              s3Base={S3}
            />
          </>
        ) : (
          <>
        {/* ── Page Header ── */}
        <div className="catmgr-header">
          <div className="catmgr-header-left">
            <h2 className="catmgr-title">
              <i className="fa-solid fa-layer-group" /> &nbsp;{gameName} — Customize
            </h2>
            <p className="catmgr-subtitle">
                  <>
                    {totalCategoryCount} item categories &nbsp;·&nbsp; {totalItems} total items
                    <br />
                    <span className="catmgr-pair-hint">
                      Add items in pairs per category (each slide needs two options). If one category has an
                      unpaired item, add the matching item there before adding to other categories.
                    </span>
                  </>
            </p>
          </div>
          <div className="catmgr-header-right">
            <button
              className="catmgr-btn catmgr-btn-outline"
              onClick={() => navigate("/admin/spot-map")}
            >
              <i className="fa-solid fa-map-location-dot" /> Customize Map
            </button>
          </div>
        </div>

        {/* ── Two-column body ── */}
        <div className="catmgr-body">

          {/* ══ LEFT: Categories list ══════════════════════════════ */}
          <div className="catmgr-left-panel">
            <div className="catmgr-panel-card">
              <div className="catmgr-panel-head">
                <span className="catmgr-panel-title">
                  <i className="fa-solid fa-list-check" /> Categories List
                </span>
                <button
                  className="catmgr-btn catmgr-btn-outline catmgr-btn-sm"
                  onClick={() => openAddListModal("categories")}
                  disabled={spotMetaSaving}
                >
                  <i className="fa-solid fa-plus" /> Add
                </button>
              </div>
              {categoriesListMeta.length === 0 && (
                <p className="catmgr-no-items catmgr-no-items--panel">No categories yet.</p>
              )}
              <ul className="catmgr-cat-list">
                {categoriesListMeta.map((item, i) => (
                  <li key={`${item.id}-${i}`} className="catmgr-cat-row">
                    {item.image ? (
                      <img
                        src={resolveAssetUrl(item.image)}
                        alt={item.name}
                        className="catmgr-list-thumb"
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      <div className="catmgr-list-thumb catmgr-list-thumb--empty">
                        <i className="fa-solid fa-image" />
                      </div>
                    )}
                    <div className="catmgr-cat-info">
                      <span className="catmgr-cat-display">{item.name}</span>
                      <span className="catmgr-cat-key">{item.id}</span>
                    </div>
                    <div className="catmgr-cat-actions">
                      <button
                        className="catmgr-icon-btn catmgr-icon-btn-sm"
                        title="Edit"
                        onClick={() => openEditListModal("categories", i, item)}
                      >
                        <i className="fa-solid fa-pen" />
                      </button>
                      <button
                        className="catmgr-icon-btn catmgr-icon-btn-sm catmgr-icon-btn-danger"
                        title="Delete"
                        onClick={() => handleDeleteListItem("categories", i)}
                      >
                        <i className="fa-solid fa-trash" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="catmgr-panel-card catmgr-panel-card--spacer">
              <div className="catmgr-panel-head">
                <span className="catmgr-panel-title">
                  <i className="fa-solid fa-earth-asia" /> Country List
                </span>
                <button
                  className="catmgr-btn catmgr-btn-outline catmgr-btn-sm"
                  onClick={() => openAddListModal("countries")}
                  disabled={spotMetaSaving}
                >
                  <i className="fa-solid fa-plus" /> Add
                </button>
              </div>
              {countriesList.length === 0 && (
                <p className="catmgr-no-items catmgr-no-items--panel">No countries yet.</p>
              )}
              <ul className="catmgr-cat-list catmgr-country-list-scroll">
                {countriesList.map((item, i) => (
                  <li key={`${item.id}-${i}`} className="catmgr-cat-row">
                    <div className="catmgr-cat-info">
                      <span className="catmgr-cat-display">{item.name}</span>
                      <span className="catmgr-cat-key">{item.id}</span>
                    </div>
                    <div className="catmgr-cat-actions">
                      <button
                        className="catmgr-icon-btn catmgr-icon-btn-sm"
                        title="Edit"
                        onClick={() => openEditListModal("countries", i, item)}
                      >
                        <i className="fa-solid fa-pen" />
                      </button>
                      <button
                        className="catmgr-icon-btn catmgr-icon-btn-sm catmgr-icon-btn-danger"
                        title="Delete"
                        onClick={() => handleDeleteListItem("countries", i)}
                      >
                        <i className="fa-solid fa-trash" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* ══ RIGHT: Category items ══════════════════════════════ */}
          <div className="catmgr-right-panel">
            {hasStructuredSpotData && (
              <div className="catmgr-card">
                <div className="catmgr-card-header">
                  <div className="catmgr-card-title-block">
                    <h3 className="catmgr-card-name">Category-wise Items</h3>
                    <span className="catmgr-badge">
                      {visibleStructuredCategories.reduce((sum, cat) => sum + cat.items.length, 0)} items
                    </span>
                  </div>
                </div>
                <div className="catmgr-structured-tabs">
                  {visibleStructuredCategories.map((cat) => (
                    <button
                      key={cat.id}
                      type="button"
                      className={`catmgr-structured-tab${activeStructuredCategory?.id === cat.id ? " catmgr-structured-tab--active" : ""}`}
                      onClick={() => setActiveStructuredTab(cat.id)}
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>
                {activeStructuredCategory && (
                  <div className="catmgr-structured-panel">
                    <div className="catmgr-card-header">
                      <div className="catmgr-card-title-block">
                        <h4 className="catmgr-card-name">{activeStructuredCategory.name}</h4>
                        <span className="catmgr-cat-key">{activeStructuredCategory.id}</span>
                        <span className="catmgr-badge">{activeStructuredCategory.items.length} items</span>
                      </div>
                      <button
                        className="catmgr-btn catmgr-btn-outline catmgr-btn-sm"
                        onClick={() => {
                          const idx = structuredCategories.findIndex((c) => c.id === activeStructuredCategory.id);
                          if (idx >= 0) openStructuredAddItemModal(idx);
                        }}
                      >
                        <i className="fa-solid fa-plus" /> Add Item
                      </button>
                    </div>
                    {activeStructuredCategory.items.length === 0 ? (
                      <p className="catmgr-no-items">No items yet.</p>
                    ) : (
                      <div className="catmgr-items-grid">
                        {activeStructuredCategory.items.map((item, itemIndex) => {
                          const catIndex = structuredCategories.findIndex((c) => c.id === activeStructuredCategory.id);
                          return (
                            <div className="catmgr-item-card" key={`${item.id}-${itemIndex}`}>
                              <div className="catmgr-item-img-wrap">
                                <ItemImage src={resolveAssetUrl(item.image)} alt={item.name} />
                              </div>
                              <div className="catmgr-item-info">
                                <span className="catmgr-item-name" title={item.name}>{item.name}</span>
                                <span className="catmgr-cat-key">
                                  {countryNameById.get(String(item.countryId || "")) || item.countryId}
                                </span>
                              </div>
                              <div className="catmgr-item-btns">
                                <button
                                  className="catmgr-icon-btn catmgr-icon-btn-sm"
                                  onClick={() => openStructuredEditItemModal(catIndex, itemIndex)}
                                  title="Edit"
                                >
                                  <i className="fa-solid fa-pen" />
                                </button>
                                <button
                                  className="catmgr-icon-btn catmgr-icon-btn-sm catmgr-icon-btn-danger"
                                  onClick={() => handleDeleteStructuredItem(catIndex, itemIndex)}
                                  title="Delete"
                                >
                                  <i className="fa-solid fa-trash" />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            {!hasStructuredSpotData && categories.length === 0 && (
              <div className="catmgr-empty">No categories found.</div>
            )}

            {!hasStructuredSpotData && categories.map((cat) => {
              const catIndex = categories.indexOf(cat);
              const visibleItems = cat.items;

              return (
                <div className="catmgr-card" key={cat.key}>
                  <div className="catmgr-card-header">
                    <div className="catmgr-card-title-block">
                      <h3 className="catmgr-card-name">{cat.displayName}</h3>
                      <span className="catmgr-card-key">{cat.key}</span>
                      <span className="catmgr-badge">{cat.items.length} items</span>
                    </div>
                    <button
                      className="catmgr-btn catmgr-btn-outline catmgr-btn-sm"
                      onClick={() => openAddItemModal(catIndex)}
                    >
                      <i className="fa-solid fa-plus" /> Add Item
                    </button>
                  </div>

                  {visibleItems.length === 0 ? (
                    <p className="catmgr-no-items">No items yet — click Add Item.</p>
                  ) : (
                    <div className="catmgr-items-grid">
                      {visibleItems.map((item) => {
                        const itemIndex = cat.items.indexOf(item);
                        return (
                          <div className="catmgr-item-card" key={itemIndex}>
                            <div className="catmgr-item-img-wrap">
                              <ItemImage
                                src={resolveAssetUrl(item.image)}
                                alt={item.name}
                              />
                            </div>
                            <div className="catmgr-item-info">
                              <span className="catmgr-item-name" title={item.name}>{item.name}</span>
                            </div>
                            <div className="catmgr-item-btns">
                              <button
                                className="catmgr-icon-btn catmgr-icon-btn-sm"
                                title="Edit"
                                onClick={() => {
                                  setItemModal({ mode: "edit", catIndex, itemIndex });
                                  setItemName(
                                    String(item.name ?? "").slice(0, ITEM_NAME_MAX_LEN)
                                  );
                                  setItemFile(null); setItemPreview(""); setItemFilename(item.image || "");
                                }}
                              >
                                <i className="fa-solid fa-pen" />
                              </button>
                              <button
                                className="catmgr-icon-btn catmgr-icon-btn-sm catmgr-icon-btn-danger"
                                title="Delete"
                                onClick={() => handleDeleteItem(catIndex, itemIndex)}
                              >
                                <i className="fa-solid fa-trash" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
          </>
        )}
      </div>

      {/* ══ Add Category Modal ══════════════════════════════════ */}
      {addCatOpen && (
        <>
          <div className="catmgr-backdrop" onClick={() => setAddCatOpen(false)} />
          <div className="catmgr-modal">
            <h3>Add Category</h3>
            <label className="catmgr-modal-label">Display name</label>
            <input
              autoFocus className="catmgr-modal-input" placeholder="e.g. Travel & Places"
              value={newCatName} onChange={(e) => setNewCatName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAddCategory()}
            />
            {newCatName.trim() && (
              <p className="catmgr-modal-hint">Key: <code>{toSlugKey(newCatName)}</code></p>
            )}
            <div className="catmgr-modal-actions">
              <button onClick={() => setAddCatOpen(false)}>Cancel</button>
              <button className="primary" onClick={handleAddCategory}>Add</button>
            </div>
          </div>
        </>
      )}

      {/* ══ Edit Category Modal ══════════════════════════════════ */}
      {editCatModal && (
        <>
          <div className="catmgr-backdrop" onClick={() => setEditCatModal(null)} />
          <div className="catmgr-modal">
            <h3>Edit Category Name</h3>
            <label className="catmgr-modal-label">Display name</label>
            <input
              autoFocus className="catmgr-modal-input" value={editCatName}
              onChange={(e) => setEditCatName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleEditCategory()}
            />
            <div className="catmgr-modal-actions">
              <button onClick={() => setEditCatModal(null)}>Cancel</button>
              <button className="primary" onClick={handleEditCategory}>Save</button>
            </div>
          </div>
        </>
      )}

      {/* ══ Add / Edit Item Modal ════════════════════════════════ */}
      {itemModal && (
        <>
          <div className="catmgr-backdrop" onClick={closeItemModal} />
          <div className="catmgr-modal catmgr-modal-wide">
            <h3>
              {itemModal.mode === "add"
                ? `Add Item — ${categories[itemModal.catIndex]?.displayName}`
                : "Edit Item"}
            </h3>

            {/* Item name */}
            <label className="catmgr-modal-label">Item name</label>
            <input
              autoFocus
              className="catmgr-modal-input"
              placeholder="e.g. Pizza forever"
              value={itemName}
              onChange={(e) => handleItemNameChange(e.target.value)}
              maxLength={ITEM_NAME_MAX_LEN}
            />
            <p className="catmgr-modal-hint" style={{ marginTop: 4 }}>
              * Maximum {ITEM_NAME_MAX_LEN} characters.
            </p>

            {/* Image upload */}
            <label className="catmgr-modal-label" style={{ marginTop: 16 }}>
              Image &nbsp;
              <span className="catmgr-optional">
                (JPEG / JPG / PNG — exactly {ITEM_W} × {ITEM_H} px)
              </span>
            </label>

            {/* Preview */}
            <div className="catmgr-upload-preview">
              {itemPreview ? (
                <img src={itemPreview} alt="preview" className="catmgr-upload-thumb" />
              ) : itemFilename ? (
                <ItemImage src={resolveAssetUrl(itemFilename)} alt="current" className="catmgr-upload-thumb" />
              ) : (
                <div className="catmgr-upload-placeholder">
                  <i className="fa-solid fa-image" />
                  <span>{ITEM_W} × {ITEM_H} px</span>
                </div>
              )}
            </div>

            <input
              ref={itemFileRef}
              type="file"
              accept=".jpeg,.jpg,.png"
              className="catmgr-file-input"
              onChange={handleItemFileChange}
            />
            <p className="catmgr-modal-hint">
              * Image required for new items. Exactly {ITEM_W} × {ITEM_H} px. Max 5 MB.
            </p>

            <div className="catmgr-modal-actions">
              <button onClick={closeItemModal} disabled={itemUploading}>Cancel</button>
              <button className="primary" onClick={handleSaveItem} disabled={itemUploading}>
                {itemUploading ? "Uploading…" : itemModal.mode === "add" ? "Add" : "Save"}
              </button>
            </div>
          </div>
        </>
      )}
      {listModal && (
        <>
          <div className="catmgr-backdrop" onClick={closeListModal} />
          <div className="catmgr-modal">
            <h3>
              {listModal.mode === "add" ? "Add" : "Edit"} {formatKey(listLabel(listModal.listType))}
            </h3>
            <label className="catmgr-modal-label">Name</label>
            <input
              autoFocus
              className="catmgr-modal-input"
              value={listNameInput}
              maxLength={listModal.listType === "countries" ? COUNTRY_NAME_MAX_LEN : CATEGORY_NAME_MAX_LEN}
              onChange={(e) => setListNameInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSaveListItem()}
            />
            <p className="catmgr-modal-hint">
              * Name must be {listModal.listType === "countries" ? COUNTRY_NAME_MIN_LEN : CATEGORY_NAME_MIN_LEN}-
              {listModal.listType === "countries" ? COUNTRY_NAME_MAX_LEN : CATEGORY_NAME_MAX_LEN} characters.
            </p>
            {listModal.listType === "categories" && (
              <>
                <label className="catmgr-modal-label" style={{ marginTop: 12 }}>
                  Category image (JPEG / JPG / PNG) {ITEM_W} × {ITEM_H} px
                </label>
                <div className="catmgr-upload-preview catmgr-upload-preview--square">
                  {listImagePreview ? (
                    <img src={listImagePreview} alt="preview" className="catmgr-upload-thumb" />
                  ) : listImageFilename ? (
                    <ItemImage src={resolveAssetUrl(listImageFilename)} alt="current" className="catmgr-upload-thumb" />
                  ) : (
                    <div className="catmgr-upload-placeholder">
                      <i className="fa-solid fa-image" />
                      <span>No image</span>
                    </div>
                  )}
                </div>
                <input
                  ref={listImageFileRef}
                  type="file"
                  accept=".jpeg,.jpg,.png"
                  className="catmgr-file-input"
                  onChange={handleListImageFileChange}
                />
                <p className="catmgr-modal-hint">* Optional now, but recommended for frontend category icon display.</p>
              </>
            )}
            <div className="catmgr-modal-actions">
              <button onClick={closeListModal} disabled={spotMetaSaving || listImageUploading}>Cancel</button>
              <button className="primary" onClick={handleSaveListItem} disabled={spotMetaSaving || listImageUploading}>
                {spotMetaSaving || listImageUploading ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </>
      )}
      {structuredItemModal && (
        <>
          <div className="catmgr-backdrop" onClick={closeStructuredItemModal} />
          <div className="catmgr-modal catmgr-modal-wide">
            <h3>{structuredItemModal.mode === "add" ? "Add Item" : "Edit Item"}</h3>
            <label className="catmgr-modal-label">Category</label>
            <select
              className="catmgr-modal-input"
              value={structuredItemCategoryId}
              onChange={(e) => setStructuredItemCategoryId(e.target.value)}
              disabled={structuredItemModal.mode === "edit"}
            >
              {visibleStructuredCategories.map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
            <label className="catmgr-modal-label" style={{ marginTop: 12 }}>Country</label>
            <select
              className="catmgr-modal-input"
              value={structuredItemCountryId}
              onChange={(e) => setStructuredItemCountryId(e.target.value)}
              disabled={structuredItemModal.mode === "edit"}
            >
              {countriesList.map((country) => (
                <option key={country.id} value={country.id}>{country.name}</option>
              ))}
            </select>
            <label className="catmgr-modal-label" style={{ marginTop: 12 }}>Item name</label>
            <input
              className="catmgr-modal-input"
              value={structuredItemName}
              maxLength={ITEM_NAME_MAX_LEN}
              onChange={(e) => setStructuredItemName(e.target.value)}
            />
            <label className="catmgr-modal-label" style={{ marginTop: 12 }}>
              Image (JPEG / JPG / PNG — exactly {ITEM_W} × {ITEM_H} px)
            </label>
            <div className="catmgr-upload-preview">
              {structuredItemPreview ? (
                <img src={structuredItemPreview} alt="preview" className="catmgr-upload-thumb" />
              ) : structuredItemFilename ? (
                <ItemImage src={resolveAssetUrl(structuredItemFilename)} alt="current" className="catmgr-upload-thumb" />
              ) : (
                <div className="catmgr-upload-placeholder">
                  <i className="fa-solid fa-image" />
                  <span>{ITEM_W} × {ITEM_H} px</span>
                </div>
              )}
            </div>
            <input
              ref={structuredItemFileRef}
              type="file"
              accept=".jpeg,.jpg,.png"
              className="catmgr-file-input"
              onChange={handleStructuredItemFileChange}
            />
            <div className="catmgr-modal-actions">
              <button onClick={closeStructuredItemModal} disabled={structuredItemUploading}>Cancel</button>
              <button className="primary" onClick={handleSaveStructuredItem} disabled={structuredItemUploading}>
                {structuredItemUploading ? "Uploading..." : "Save"}
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default CustomizeSpotTheme;
